/**
 * Accent icons — one icon and one color per part of a record screen, shown on
 * its stat tile and again in its section's header band so the two read as a
 * pair (design system "Iconography"; shop staff like the app's icons). One
 * source for web and mobile: icons are Lucide NAMES here; web maps them to
 * `lucide-react`, the app to `lucide-react-native`.
 *
 * Add a part (technicians, photos) by adding a row here, never by picking a
 * color on a screen. These are not the shop's section icons (SectionTile).
 *
 * Only a type import, so React Native can deep-import it
 * (`@turn-connect/ui/src/theme/accents`).
 */
import type { ColorTokens } from './tokens';

export type AccentKey =
  | 'open'
  | 'past'
  | 'vehicles'
  | 'invoices'
  | 'attention'
  // My Work's sections (My work orders reuses `open`).
  | 'mytime'
  | 'approvals'
  | 'timesheets'
  | 'mentions'
  | 'support'
  // The Accounts dashboard's list of accounts, groups and locations.
  | 'locations';

/** Lucide icon names (kebab-case, as in the Lucide catalogue). */
export type AccentIconName =
  | 'wrench'
  | 'circle-check'
  | 'car'
  | 'receipt'
  | 'circle-alert'
  | 'clock'
  | 'clipboard-check'
  | 'calendar-check'
  | 'at-sign'
  | 'life-buoy'
  | 'store';

export interface AccentDef {
  icon: AccentIconName;
  color: (t: ColorTokens) => string;
  tint: (t: ColorTokens) => string;
}

export const ACCENTS: Record<AccentKey, AccentDef> = {
  open: { icon: 'wrench', color: (t) => t.info, tint: (t) => t.infoSubtle },
  past: { icon: 'circle-check', color: (t) => t.success, tint: (t) => t.successSubtle },
  vehicles: { icon: 'car', color: (t) => t.category.teal, tint: (t) => t.category.tealSubtle },
  invoices: { icon: 'receipt', color: (t) => t.category.purple, tint: (t) => t.category.purpleSubtle },
  // Grey when there is nothing waiting (see resolveAccent's `active`).
  attention: { icon: 'circle-alert', color: (t) => t.warning, tint: (t) => t.warningSubtle },
  // My Work: your own hours, work orders waiting for your OK (they need you,
  // hence warning), timesheets to approve, @mentions, help tickets.
  mytime: { icon: 'clock', color: (t) => t.category.indigo, tint: (t) => t.category.indigoSubtle },
  approvals: { icon: 'clipboard-check', color: (t) => t.warning, tint: (t) => t.warningSubtle },
  timesheets: { icon: 'calendar-check', color: (t) => t.category.teal, tint: (t) => t.category.tealSubtle },
  mentions: { icon: 'at-sign', color: (t) => t.category.pink, tint: (t) => t.category.pinkSubtle },
  support: { icon: 'life-buoy', color: (t) => t.category.purple, tint: (t) => t.category.purpleSubtle },
  locations: { icon: 'store', color: (t) => t.brand, tint: (t) => t.brandSubtle },
};

export interface ResolvedAccent {
  icon: AccentIconName;
  color: string;
  tint: string;
}

/** An accent's icon and colors in a theme; `active: false` greys it (a count of 0 that would otherwise need attention). */
export function resolveAccent(t: ColorTokens, key: AccentKey, active = true): ResolvedAccent {
  const a = ACCENTS[key];
  if (!active) return { icon: a.icon, color: t.text.muted, tint: t.washStrong };
  return { icon: a.icon, color: a.color(t), tint: a.tint(t) };
}

/** Accent icon sizes: `tile` in stat tiles and lists, `band` in a section's header band. Stroke 2. */
export const accentSizes = {
  tile: { box: 32, icon: 18, radius: 6 },
  band: { box: 40, icon: 22, radius: 8 },
} as const;

export type AccentSize = keyof typeof accentSizes;
